import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  MapPin,
  Satellite,
  Radio,
  Bluetooth,
  Smartphone,
  Shield,
  CheckCircle,
  ArrowRight,
  Truck,
  Package,
  Factory,
  Thermometer,
  AlertTriangle,
  BarChart3,
  Globe,
  Cpu,
  Battery,
  Clock,
  DollarSign,
  Search,
  Lock,
  Zap,
  TrendingUp,
  Building2,
  Container
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/Header';
import { TopBar } from '@/components/TopBar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/sections/ContactForm';
import { HreflangTags } from '@/components/HreflangTags';
import { useLanguage } from '@/i18n/LanguageContext';

const AssetTrackingPage = () => {
  const { t } = useLanguage();
  
  return (
    <>
      <Helmet>
        <title>{t('assetTrackingPage.metaTitle')}</title>
        <meta name="description" content={t('assetTrackingPage.metaDesc')} />
        <meta name="keywords" content="asset tracking, IoT asset tracking, industrial asset tracking, logistics asset tracking, satellite asset tracking, cold chain monitoring, equipment tracking, construction equipment tracking, multi connectivity IoT, GPS tracking, LoRaWAN tracking" />
        <link rel="canonical" href="https://0gbaltics.com/asset-tracking" />
        <meta property="og:title" content={t('assetTrackingPage.metaTitle')} />
        <meta property="og:description" content={t('assetTrackingPage.metaDesc')} />
        <meta property="og:type" content="website" />
      </Helmet>
      <HreflangTags />

      <TopBar />
      <Header />

      <main className="pt-20">
        
        {/* ========================================= */}
        {/* HERO SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="asset-tracking-hero">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('assetTrackingPage.heroBadge')}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  {t('assetTrackingPage.heroTitle')}
                </h1>
                <p className="text-xl text-slate-300 mb-6 leading-relaxed">
                  {t('assetTrackingPage.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-4 mb-8">
                  <Button 
                    size="lg"
                    className="bg-teal-500 hover:bg-teal-600 text-white"
                    onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    {t('assetTrackingPage.ctaButton')}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
                
                {/* Technology badges */}
                <div className="flex flex-wrap gap-3">
                  {['Satellite', 'LTE / NB-IoT', 'LoRaWAN', 'BLE', 'Mobile Gateways'].map((tech, i) => (
                    <span key={i} className="bg-slate-800 border border-slate-700 text-slate-300 text-sm px-3 py-1 rounded-full">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <img 
                  src="/assets/images/asset-tracking/ecosystem-diagram.png" 
                  alt="IoT Asset Tracking Ecosystem - devices, connectivity, cloud, analytics"
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHAT IS ASSET TRACKING */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="what-is-tracking">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('assetTrackingPage.whatIsBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('assetTrackingPage.whatIsTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('assetTrackingPage.whatIsDesc')}
                </p>
                
                <h3 className="text-lg font-semibold text-slate-900 mb-4">{t('assetTrackingPage.assetsTitle')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: Factory, text: t('assetTrackingPage.asset1') },
                    { icon: Container, text: t('assetTrackingPage.asset2') },
                    { icon: Truck, text: t('assetTrackingPage.asset3') },
                    { icon: Package, text: t('assetTrackingPage.asset4') },
                    { icon: Shield, text: t('assetTrackingPage.asset5') },
                    { icon: Thermometer, text: t('assetTrackingPage.asset6') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <Icon className="h-5 w-5 text-teal-600" />
                        <span className="text-slate-700">{item.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div className="bg-slate-50 p-8 rounded-lg">
                <img 
                  src="/assets/images/asset-tracking/campus-tracking.png" 
                  alt="Industrial campus with IoT tracking coverage showing vehicles, tools and equipment"
                  className="w-full h-auto rounded-lg mb-6"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* WHY ASSET TRACKING MATTERS */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="why-tracking-matters">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                {t('assetTrackingPage.whyBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                {t('assetTrackingPage.whyTitle')}
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed">
                {t('assetTrackingPage.whyDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { title: t('assetTrackingPage.benefit1'), desc: t('assetTrackingPage.benefit1Desc'), icon: Shield },
                { title: t('assetTrackingPage.benefit2'), desc: t('assetTrackingPage.benefit2Desc'), icon: TrendingUp },
                { title: t('assetTrackingPage.benefit3'), desc: t('assetTrackingPage.benefit3Desc'), icon: Clock },
                { title: t('assetTrackingPage.benefit4'), desc: t('assetTrackingPage.benefit4Desc'), icon: MapPin },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="bg-slate-800 p-6 rounded-lg">
                    <Icon className="h-8 w-8 text-teal-400 mb-4" />
                    <h4 className="font-semibold text-white mb-2">{item.title}</h4>
                    <p className="text-slate-400 text-sm">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* MULTI-CONNECTIVITY */}
        {/* ========================================= */}
        <section id="technologies" className="py-20 md:py-32 bg-white" data-testid="multi-connectivity">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <img 
                  src="/assets/images/asset-tracking/multi-connectivity.png" 
                  alt="Multi-connectivity IoT tracking across factory, city and remote environments"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
              
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('assetTrackingPage.networkBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('assetTrackingPage.networkTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('assetTrackingPage.networkDesc')}
                </p>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  {t('assetTrackingPage.networkDesc2')}
                </p>
                
                <div className="space-y-4">
                  {[
                    { icon: Radio, name: t('assetTrackingPage.lorawanName'), desc: t('assetTrackingPage.lorawanDesc'), color: 'bg-blue-600' },
                    { icon: Smartphone, name: t('assetTrackingPage.nbiotName'), desc: t('assetTrackingPage.nbiotDesc'), color: 'bg-green-600' },
                    { icon: Bluetooth, name: t('assetTrackingPage.bleName'), desc: t('assetTrackingPage.bleDesc'), color: 'bg-purple-600' },
                    { icon: Satellite, name: t('assetTrackingPage.satelliteName'), desc: t('assetTrackingPage.satelliteDesc'), color: 'bg-orange-600' },
                  ].map((tech, i) => {
                    const Icon = tech.icon;
                    return (
                      <div key={i} className="flex items-center gap-4 bg-slate-50 p-4 rounded-lg">
                        <div className={`w-12 h-12 ${tech.color} rounded-lg flex items-center justify-center`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{tech.name}</h4>
                          <p className="text-slate-600 text-sm">{tech.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* TRACKING DEVICES */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="tracking-devices">
          <div className="container-0g">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                {t('assetTrackingPage.devicesBadge')}
              </span>
              <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                {t('assetTrackingPage.devicesTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('assetTrackingPage.devicesDesc')}
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-6 mb-12">
              {[
                { icon: MapPin, title: t('assetTrackingPage.gpsTracking'), desc: t('assetTrackingPage.gpsTrackingDesc') },
                { icon: Satellite, title: t('assetTrackingPage.satelliteConnectivity'), desc: t('assetTrackingPage.satelliteConnectivityDesc') },
                { icon: Smartphone, title: t('assetTrackingPage.cellularNetworks'), desc: t('assetTrackingPage.cellularNetworksDesc') },
                { icon: Battery, title: t('assetTrackingPage.multiYearBattery'), desc: t('assetTrackingPage.multiYearBatteryDesc') },
              ].map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="bg-white border border-slate-200 p-6 rounded-lg text-center">
                    <Icon className="h-10 w-10 text-teal-600 mx-auto mb-4" />
                    <h4 className="font-semibold text-slate-900 mb-2">{item.title}</h4>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                );
              })}
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                { 
                  name: t('assetTrackingPage.compactTracker'),
                  desc: t('assetTrackingPage.compactTrackerDesc'),
                  tags: ['LTE-M', 'NB-IoT', 'IP67'],
                  image: '/assets/images/ble-trackers/pro-asset-tag.png'
                },
                { 
                  name: t('assetTrackingPage.longLifeTracker'),
                  desc: t('assetTrackingPage.longLifeTrackerDesc'),
                  tags: ['GPS', 'BLE Gateway', '10+ Years'],
                  image: '/assets/images/ble-trackers/m1-coin-beacon.png'
                },
                { 
                  name: t('assetTrackingPage.globalSatelliteTracker'),
                  desc: t('assetTrackingPage.globalSatelliteTrackerDesc'),
                  tags: ['Satellite', '4G LTE', 'BLE'],
                  image: '/assets/images/ble-trackers/industrial-tracker.png'
                },
              ].map((device, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-100 p-6 flex items-center justify-center h-48">
                    <img src={device.image} alt={device.name} className="max-h-full w-auto object-contain" />
                  </div>
                  <div className="p-6">
                    <h4 className="font-semibold text-slate-900 mb-2">{device.name}</h4>
                    <p className="text-slate-600 text-sm mb-4">{device.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {device.tags.map((tag, j) => (
                        <span key={j} className="bg-teal-100 text-teal-700 text-xs px-2 py-1 rounded">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CAMPUS TRACKING */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="campus-tracking">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('assetTrackingPage.campusBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('assetTrackingPage.campusTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('assetTrackingPage.campusDesc')}
                </p>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {t('assetTrackingPage.campusDesc2')}
                </p>
                
                <div className="bg-slate-900 p-6 rounded-lg">
                  <h4 className="text-white font-semibold mb-4">{t('assetTrackingPage.campusDeployments')}</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      t('assetTrackingPage.campusSite1'),
                      t('assetTrackingPage.campusSite2'),
                      t('assetTrackingPage.campusSite3'),
                      t('assetTrackingPage.campusSite4'),
                      t('assetTrackingPage.campusSite5'),
                      t('assetTrackingPage.campusSite6')
                    ].map((site, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-teal-400" />
                        <span className="text-slate-300 text-sm">{site}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-600 mb-4">
                  {t('assetTrackingPage.multiSiteBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('assetTrackingPage.multiSiteTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('assetTrackingPage.multiSiteDesc')}
                </p>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {t('assetTrackingPage.multiSiteDesc2')}
                </p>
                
                <div className="bg-teal-50 border border-teal-200 p-6 rounded-lg">
                  <h4 className="text-teal-800 font-semibold mb-2">{t('assetTrackingPage.realImpact')}</h4>
                  <p className="text-teal-700 text-sm">
                    {t('assetTrackingPage.realImpactDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* GLOBAL TRACKING */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-900" data-testid="global-tracking">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">
                  {t('assetTrackingPage.globalBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-white mb-6">
                  {t('assetTrackingPage.globalTitle')}
                </h2>
                <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                  {t('assetTrackingPage.globalDesc')}
                </p>
                <p className="text-slate-400 mb-8 leading-relaxed">
                  {t('assetTrackingPage.globalDesc2')}
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { stat: t('assetTrackingPage.globalStat1'), label: t('assetTrackingPage.globalStat1Label') },
                    { stat: t('assetTrackingPage.globalStat2'), label: t('assetTrackingPage.globalStat2Label') },
                    { stat: t('assetTrackingPage.globalStat3'), label: t('assetTrackingPage.globalStat3Label') },
                    { stat: t('assetTrackingPage.globalStat4'), label: t('assetTrackingPage.globalStat4Label') },
                  ].map((item, i) => (
                    <div key={i} className="bg-slate-800 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold text-teal-400">{item.stat}</div>
                      <p className="text-slate-400 text-sm">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <img 
                  src="/assets/images/asset-tracking/global-map.png" 
                  alt="Global asset tracking map showing worldwide logistics and satellite coverage"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* COLD CHAIN MONITORING */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-white" data-testid="cold-chain">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <img 
                  src="/assets/images/asset-tracking/cold-chain.png" 
                  alt="Cold chain monitoring with IoT sensors, refrigerated truck and temperature dashboard"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </div>
              
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-blue-600 mb-4">
                  {t('assetTrackingPage.coldChainBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('assetTrackingPage.coldChainTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('assetTrackingPage.coldChainDesc')}
                </p>
                <p className="text-slate-600 mb-6 leading-relaxed">
                  {t('assetTrackingPage.coldChainDesc2')}
                </p>
                
                <div className="space-y-4">
                  {[
                    { icon: Thermometer, title: t('assetTrackingPage.tempLogging'), desc: t('assetTrackingPage.tempLoggingDesc') },
                    { icon: AlertTriangle, title: t('assetTrackingPage.thresholdAlerts'), desc: t('assetTrackingPage.thresholdAlertsDesc') },
                    { icon: Clock, title: t('assetTrackingPage.chainCustody'), desc: t('assetTrackingPage.chainCustodyDesc') },
                    { icon: Shield, title: t('assetTrackingPage.regulatoryCompliance'), desc: t('assetTrackingPage.regulatoryComplianceDesc') },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{item.title}</h4>
                          <p className="text-slate-600 text-sm">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* ASSET RECOVERY & THEFT */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-slate-50" data-testid="asset-recovery">
          <div className="container-0g">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-red-600 mb-4">
                  {t('assetTrackingPage.securityBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('assetTrackingPage.securityTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('assetTrackingPage.securityDesc')}
                </p>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  {t('assetTrackingPage.securityDesc2')}
                </p>
                
                <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
                  <h4 className="text-red-800 font-semibold mb-4">{t('assetTrackingPage.theftPrevention')}</h4>
                  <div className="space-y-2">
                    {[
                      t('assetTrackingPage.theftFeature1'),
                      t('assetTrackingPage.theftFeature2'),
                      t('assetTrackingPage.theftFeature3'),
                      t('assetTrackingPage.theftFeature4'),
                      t('assetTrackingPage.theftFeature5'),
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-red-600" />
                        <span className="text-red-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div>
                <span className="inline-block text-xs font-bold uppercase tracking-widest text-purple-600 mb-4">
                  {t('assetTrackingPage.analyticsBadge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                  {t('assetTrackingPage.analyticsTitle')}
                </h2>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {t('assetTrackingPage.analyticsDesc')}
                </p>
                
                <img 
                  src="/assets/images/asset-tracking/ai-dashboard.png" 
                  alt="AI analytics dashboard showing asset locations, utilization statistics and alerts"
                  className="w-full h-auto rounded-lg shadow-lg mb-6"
                />
                
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { title: t('assetTrackingPage.utilizationStats'), desc: t('assetTrackingPage.utilizationStatsDesc') },
                    { title: t('assetTrackingPage.predictiveMaintenance'), desc: t('assetTrackingPage.predictiveMaintenanceDesc') },
                    { title: t('assetTrackingPage.fleetOptimization'), desc: t('assetTrackingPage.fleetOptimizationDesc') },
                    { title: t('assetTrackingPage.customReporting'), desc: t('assetTrackingPage.customReportingDesc') },
                  ].map((item, i) => (
                    <div key={i} className="bg-white border border-slate-200 p-4 rounded-lg">
                      <h5 className="font-semibold text-slate-900 text-sm mb-1">{item.title}</h5>
                      <p className="text-slate-600 text-xs">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================= */}
        {/* CTA SECTION */}
        {/* ========================================= */}
        <section className="py-20 md:py-32 bg-teal-600" data-testid="cta-section">
          <div className="container-0g text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('assetTrackingPage.finalCtaTitle')}
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto">
              {t('assetTrackingPage.finalCtaDesc')}
            </p>
            <Button 
              size="lg"
              className="bg-white text-teal-700 hover:bg-teal-50 font-semibold"
              onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('assetTrackingPage.finalCtaButton')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* ========================================= */}
        {/* CONTACT FORM */}
        {/* ========================================= */}
        <div id="contact-form">
          <ContactForm />
        </div>

      </main>

      <Footer />
    </>
  );
};

export default AssetTrackingPage;
